import React, { useState } from 'react';
import styles from './ChatMessage.module.scss';
import ChatBotImage from 'app/assets/ChatBot.svg';
import classnames from 'classnames';
import { getUserName, useAppSelector } from 'app/redux';
import { getInitials } from 'app/utils';
import { IChatMessage } from './ChatMessage.interface';
import { Button } from '@zurich-primitives/button';
import { Trash } from '@zurich-data-dsm-icons/action.trash';
import { LoadingSpinner } from 'app/components';
import { ThumbsFeedback } from '@zurich-zurichat/thumbs-feedback';
import { FeedbackModal } from 'app/components/Modals';

export const ChatMessage: React.FC<IChatMessage> = ({
  type,
  message,
  timestamp,
  onDelete,
  canDelete,
  onFeedback,
  showFeedback
}) => {
  const username = useAppSelector(getUserName);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative'>(
    'positive'
  );
  const [thumbsKey, setThumbsKey] = useState(0);

  const handleDelete = (): void => {
    if (canDelete && timestamp) {
      onDelete(timestamp);
    }
  };

  const handleThumbUp = (): void => {
    setFeedbackType('positive');
    setIsFeedbackModalOpen(true);
  };

  const handleThumbDown = (): void => {
    setFeedbackType('negative');
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = (
    feedback: string,
    selectedOptions: string[],
    reason: string
  ): void => {
    if (!onFeedback) return;
    let fullFeedback = `${feedback} - Options: ${selectedOptions.join(', ')}`;
    if (reason) {
      fullFeedback += ` - Reason: ${reason}`;
    }
    onFeedback(fullFeedback, message);
  };

  const handleModalClose = (cancelled: boolean): void => {
    if (cancelled) {
      setThumbsKey((prev) => prev + 1);
    }
    setIsFeedbackModalOpen(false);
  };

  const renderLoadingSpinner = (): React.JSX.Element => (
    <LoadingSpinner
      containerStyle={{
        position: 'relative',
        left: '1.8rem',
        width: '0rem',
        height: '0rem',
        backdropFilter: 'unset'
      }}
      imageStyle={{ width: '6rem', height: '2rem', left: '2rem' }}
    />
  );

  return (
    <section
      className={styles['chat-message-wrapper']}
      aria-label="Chat message"
      onMouseEnter={() => setShowDeleteButton(true)}
      onMouseLeave={() => setShowDeleteButton(false)}
    >
      <div
        className={classnames(styles.icon, {
          [styles.avatar]: type === 'question'
        })}
      >
        {type === 'answer' ? (
          <img src={ChatBotImage} alt="ChatBot Avatar" />
        ) : (
          username && getInitials(username)
        )}
      </div>

      <div
        className={classnames(styles.message, {
          [styles['user-message']]: type === 'question',
          [styles['bot-message']]: type === 'answer' && message !== 'loading',
          [styles.loading]: message === 'loading'
        })}
      >
        {message === 'loading' ? renderLoadingSpinner() : message}

        {canDelete && type === 'question' && (
          <Button
            variant="icon"
            size="small"
            aria-label="Delete message"
            className={classnames(styles['delete-button'], {
              [styles.visible]: showDeleteButton
            })}
            onClick={handleDelete}
          >
            <Button.Icon>
              <Trash />
            </Button.Icon>
          </Button>
        )}

        {showFeedback && (
          <div className={styles['feedback-container']}>
            <ThumbsFeedback
              key={thumbsKey}
              onThumbUp={handleThumbUp}
              onThumbDown={handleThumbDown}
            />
          </div>
        )}
      </div>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={handleModalClose}
        onSubmit={handleFeedbackSubmit}
        feedbackType={feedbackType}
      />
    </section>
  );
};
